/**
 * BLRRD Selection — magic-wand.js  (Phase 4.B.select.2, Wave 8.2)
 *
 * Color-similarity selection — the "Fuzzy Select" / "Magic Wand" tool.
 * Two modes:
 *
 *   • Contiguous (default)  — 4-connected BFS flood-fill starting from
 *     the seed pixel, gathering every pixel within `tolerance` Lab ΔE76
 *     units of the seed colour.
 *
 *   • Global (`contiguous = false`) — no BFS; every pixel with
 *     ΔE76(seed) < tolerance is selected, regardless of connectivity.
 *     This is the "Select by Color" or "Select Similar" command.
 *
 * Tolerance is expressed in ΔE76 (the original CIE 1976 colour
 * difference): `sqrt(ΔL² + Δa² + Δb²)` between the seed Lab and the
 * candidate Lab. ΔE76 is perceptually uniform enough for the wand UX,
 * and substantially cheaper than ΔE2000. Default tolerance is 10 (a
 * "just-noticeable difference" is ≈ 2.3 ΔE76; 10 ΔE76 ≈ "a few JNDs",
 * a useful starting point for skies, walls, and other smooth regions).
 *
 * Anti-aliasing
 * ─────────────
 * When `antiAlias` is enabled (default), pixels at the boundary of the
 * tolerance band receive partial coverage: `1 - clamp((ΔE - tolMin) / (tolMax - tolMin), 0, 1)`
 * with `tolMin = 0.75·tolerance`, `tolMax = tolerance`. Pixels with
 * ΔE < tolMin are fully selected; pixels with ΔE > tolMax are not
 * selected at all. The 25% transition band gives 1-pixel-soft edges
 * without smearing into the actual region.
 *
 * Provenance
 * ──────────
 * Clean-room from CIE Pub. 15:2018 (Lab + ΔE76) and the textbook
 * 4-connected BFS flood-fill (Foley/van Dam Ch. 19). NO GIMP/GEGL
 * source consulted. The Lab conversion is shared with
 * `src/tools/eyedropper.js::linRGBtoLab` (used for the eyedropper +
 * delta-E readouts already) — we import it instead of duplicating.
 *
 * Source-space note
 * ─────────────────
 * The input buffer is the editor's canonical `E.srcFloat` — Float32
 * RGBA in scene-linear ACEScg. We convert to linear sRGB via
 * `acescgToLinSRGB` (the same matrix the eyedropper uses), then to
 * Lab via D65 XYZ. ACEScg → sRGB-linear can produce negative or
 * super-1 values (gamut excursions); the Lab conversion handles them
 * gracefully (the cube-root branch is defined for negative t in the
 * eyedropper's helper, with the linear-segment fallback below δ³).
 */

import { linRGBtoLab } from '../tools/eyedropper.js';
import { M_ACEScg_to_sRGB } from '../color/spaces.js';

/* ACEScg triplet → linear sRGB triplet (no encode). Identical to the
 * helper inside eyedropper.js — duplicated locally so this module
 * stays callable from src/selection/ in isolation. */
function acescgToLinSRGB(r, g, b) {
    const m = M_ACEScg_to_sRGB;
    return [
        m[0] * r + m[1] * g + m[2] * b,
        m[3] * r + m[4] * g + m[5] * b,
        m[6] * r + m[7] * g + m[8] * b,
    ];
}

/**
 * Compute Lab for a single ACEScg pixel.
 *
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {[number, number, number]}
 */
function acescgPixelToLab(r, g, b) {
    const [lR, lG, lB] = acescgToLinSRGB(r, g, b);
    /* Clamp negative — Lab is conceptually defined on linear sRGB in
     * [0,1]; the wand is a perceptual tool, super-1/negative values
     * (specular highlights, ACEScg gamut excursions) should clip to
     * the visible spectrum for ΔE purposes. */
    const cR = lR < 0 ? 0 : (lR > 1 ? 1 : lR);
    const cG = lG < 0 ? 0 : (lG > 1 ? 1 : lG);
    const cB = lB < 0 ? 0 : (lB > 1 ? 1 : lB);
    return linRGBtoLab(cR, cG, cB);
}

/**
 * ΔE76 (CIE 1976) — Euclidean distance in Lab. Symmetric.
 *
 * @param {Array<number>} labA
 * @param {Array<number>} labB
 * @returns {number}
 */
function deltaE76(labA, labB) {
    const dL = labA[0] - labB[0];
    const da = labA[1] - labB[1];
    const db = labA[2] - labB[2];
    return Math.sqrt(dL * dL + da * da + db * db);
}

/**
 * Build a Lab-space "image" cache (Float32Array(w*h*3)) so the BFS
 * doesn't recompute Lab on every visit. ~12 MB per 4K image but
 * worth it for the per-pixel sqrt + cbrt cost (Lab forward is the
 * hot path).
 *
 * @param {Float32Array} rgba   ACEScg RGBA, length = w*h*4
 * @param {number}        w
 * @param {number}        h
 * @returns {Float32Array}
 */
function buildLabCache(rgba, w, h) {
    const N = w * h;
    const lab = new Float32Array(N * 3);
    for (let i = 0, j = 0, k = 0; i < N; i++, j += 4, k += 3) {
        const [L, a, b] = acescgPixelToLab(rgba[j], rgba[j + 1], rgba[j + 2]);
        lab[k]     = L;
        lab[k + 1] = a;
        lab[k + 2] = b;
    }
    return lab;
}

/**
 * Compute partial coverage at the edge of the tolerance band.
 *
 * Edge case — tolerance = 0
 * ─────────────────────────
 * With tol = 0 the AA band collapses (tolMin = tolMax = 0). The
 * "exact-match" semantics users expect is: ΔE = 0 → coverage 1.0;
 * any difference → 0. We special-case the degenerate band so the
 * tolerance=0 wand still selects all pixels with identical Lab to
 * the seed instead of selecting nothing (Codex 4.B.select.2 STOP-2).
 *
 * @param {number} deltaE   ΔE76 from the seed
 * @param {number} tolMin
 * @param {number} tolMax
 * @returns {number}        coverage in [0, 1]
 */
function bandCoverage(deltaE, tolMin, tolMax) {
    if (tolMax === 0) return deltaE === 0 ? 1 : 0;
    if (deltaE <= tolMin) return 1;
    if (deltaE >= tolMax) return 0;
    const t = (deltaE - tolMin) / (tolMax - tolMin);
    return 1 - t;       /* linear ramp; smooth-step would over-soften 1-px edges */
}

/**
 * Magic-wand select.
 *
 * Returns a fresh Float32Array(w*h) coverage mask. The caller is
 * responsible for combining it into the active selection via
 * `src/selection/combine.js::combine()` with the modifier-key op.
 *
 * @param {Float32Array} rgba       ACEScg RGBA buffer, length = w*h*4
 * @param {number}        w
 * @param {number}        h
 * @param {number}        x0        seed x (integer)
 * @param {number}        y0        seed y (integer)
 * @param {number}        tolerance ΔE76 threshold (default 10)
 * @param {object}        [opts]
 * @param {boolean}       [opts.contiguous=true]   4-connected BFS
 * @param {boolean}       [opts.antiAlias=true]    soft 25% band edge
 * @returns {Float32Array}            fresh mask
 */
export function magicWand(rgba, w, h, x0, y0, tolerance = 10, opts = {}) {
    if (!(rgba instanceof Float32Array)) {
        throw new Error('magicWand: rgba must be Float32Array');
    }
    const W = w | 0, H = h | 0;
    if (rgba.length < W * H * 4) {
        throw new Error(`magicWand: buffer too short (${rgba.length}, expected ≥ ${W * H * 4})`);
    }
    const sx = Math.max(0, Math.min(W - 1, Math.round(x0)));
    const sy = Math.max(0, Math.min(H - 1, Math.round(y0)));
    const tol = Math.max(0, Number(tolerance) || 0);
    const contiguous = opts.contiguous !== false;
    const antiAlias  = opts.antiAlias  !== false;

    const tolMin = antiAlias ? tol * 0.75 : tol;
    const tolMax = tol;

    const mask = new Float32Array(W * H);
    if (tol === 0) {
        /* Exact-match wand: only pixels with identical Lab triplets
         * to the seed are selected. Edge cases handled below. */
    }

    /* Per-pixel Lab cache. */
    const lab = buildLabCache(rgba, W, H);
    const seedIdx = sy * W + sx;
    const seedLab = [lab[seedIdx * 3], lab[seedIdx * 3 + 1], lab[seedIdx * 3 + 2]];

    if (!contiguous) {
        /* Global mode — visit every pixel, no BFS. */
        for (let i = 0; i < W * H; i++) {
            const k = i * 3;
            const dE = deltaE76(seedLab, [lab[k], lab[k + 1], lab[k + 2]]);
            mask[i] = bandCoverage(dE, tolMin, tolMax);
        }
        return mask;
    }

    /* Contiguous mode — 4-connected BFS from the seed. We use a flat
     * Int32Array queue (no per-step allocation) and a Uint8Array
     * "visited" bitmap. */
    const visited = new Uint8Array(W * H);
    const queue = new Int32Array(W * H);
    let qHead = 0, qTail = 0;

    /* Seed enters with coverage 1.0 by definition (ΔE=0). */
    mask[seedIdx] = 1;
    visited[seedIdx] = 1;
    queue[qTail++] = seedIdx;

    while (qHead < qTail) {
        const idx = queue[qHead++];
        const py = (idx / W) | 0;
        const px = idx - py * W;

        /* Visit the 4 neighbours. */
        if (px > 0)     enqueue(idx - 1);
        if (px < W - 1) enqueue(idx + 1);
        if (py > 0)     enqueue(idx - W);
        if (py < H - 1) enqueue(idx + W);
    }

    function enqueue(nIdx) {
        if (visited[nIdx]) return;
        visited[nIdx] = 1;
        const k = nIdx * 3;
        const dE = deltaE76(seedLab, [lab[k], lab[k + 1], lab[k + 2]]);
        const cov = bandCoverage(dE, tolMin, tolMax);
        if (cov <= 0) return;            /* outside band — don't even queue */
        mask[nIdx] = cov;
        /* Only fully-covered pixels propagate the BFS — the edge band
         * marks coverage but does not push the frontier further
         * (otherwise a single noisy edge pixel could spawn a
         * cascading partial-cover halo). The `tol === 0` degenerate
         * case satisfies `cov === 1` for exact-match neighbours so
         * the BFS still propagates across an identical-Lab region. */
        if (cov >= 1) {
            queue[qTail++] = nIdx;
        }
    }

    return mask;
}

/* Re-export the ΔE76 helper for external diagnostics + test gates. */
export { deltaE76, acescgPixelToLab };
